import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useNavGuard } from '../../contexts/NavGuardContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared save-lifecycle hook for Price Book calculators.
 *
 *   const {
 *     saveId, name, setName, estimateId, setEstimateId,
 *     isSaved, isDirty, loading,
 *     save, pushToEstimate, load,
 *   } = useCalculatorSave({
 *     calculatorType: 'concrete',
 *     getConfig: () => ({ ...state fields... }),
 *     applyConfig: (cfg) => { set fields from cfg },
 *     getSummary: () => ({ text, totals }),
 *   });
 *
 * Mirrors the estimate editor save UX: Save button darkens when clean,
 * lightens when dirty; navGuard fires on unsaved changes; URL sync
 * (`?save=<id>`) so reopening a saved calc re-hydrates the state.
 */
export default function useCalculatorSave({
  calculatorType, getConfig, applyConfig, getSummary,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSaveId = searchParams.get('save') || null;

  const [saveId, setSaveId] = useState(urlSaveId);
  const [name, setName] = useState('');
  const [estimateId, setEstimateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { registerGuard } = useNavGuard();

  // Baseline signature of config when last saved (or loaded). Used
  // to decide `isDirty` without us having to micro-track every edit.
  const baselineRef = useRef(null);

  const currentSig = useMemo(() => {
    try {
      return JSON.stringify({ name, estimateId, config: getConfig() });
    } catch {
      return '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, estimateId, getConfig]);

  const isDirty = baselineRef.current !== null && currentSig !== baselineRef.current;
  const isSaved = saveId !== null && !isDirty;

  // ── Initial load if ?save=<id> is in the URL ────────────────
  useEffect(() => {
    if (!urlSaveId) {
      // Treat as fresh: baseline is "nothing saved yet" — no dirty until user types.
      baselineRef.current = currentSig;
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/price-book/saves/${urlSaveId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(doc => {
        if (cancelled || !doc) return;
        setSaveId(doc.save_id);
        setName(doc.name || '');
        setEstimateId(doc.estimate_id || null);
        if (doc.config && applyConfig) applyConfig(doc.config);
        // Next render the config will reflect the loaded state; snapshot
        // the fresh signature in a microtask so isDirty stays false.
        setTimeout(() => {
          try {
            baselineRef.current = JSON.stringify({
              name: doc.name || '',
              estimateId: doc.estimate_id || null,
              config: getConfig(),
            });
          } catch { /* no-op */ }
        }, 0);
      })
      .catch(() => toast.error('Could not load saved calculation'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSaveId]);

  // ── Nav guard: prompt on unsaved changes ────────────────────
  useEffect(() => {
    if (!registerGuard) return undefined;
    const unregister = registerGuard(async () => {
      if (!isDirty) return true;
      return window.confirm(
        'You have unsaved changes in this calculator. Leave without saving?'
      );
    });
    return unregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // Beforeunload — browser close / refresh / back-button
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // ── Save ─────────────────────────────────────────────────────
  const save = useCallback(async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      toast.error('Give this calculation a name before saving');
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        name: trimmed,
        calculator_type: calculatorType,
        config: getConfig(),
        summary_text: getSummary ? getSummary().text : null,
        summary_totals: getSummary ? getSummary().totals : null,
        estimate_id: estimateId || null,
      };
      const url = saveId
        ? `${API}/price-book/saves/${saveId}`
        : `${API}/price-book/saves`;
      const method = saveId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text();
        toast.error(`Save failed — ${err.slice(0, 80)}`);
        return null;
      }
      const doc = await res.json();
      setSaveId(doc.save_id);
      // Reflect freshly saved state in URL so refresh / bookmark works
      if (doc.save_id && doc.save_id !== urlSaveId) {
        const next = new URLSearchParams(searchParams);
        next.set('save', doc.save_id);
        setSearchParams(next, { replace: true });
      }
      // Snapshot new baseline = "clean"
      baselineRef.current = JSON.stringify({
        name: doc.name, estimateId: doc.estimate_id || null, config: getConfig(),
      });
      toast.success('Saved to Price Book');
      return doc;
    } finally {
      setSaving(false);
    }
  }, [name, estimateId, calculatorType, saveId, searchParams, setSearchParams, getConfig, getSummary, urlSaveId]);

  // ── Push to estimate ────────────────────────────────────────
  const pushToEstimate = useCallback(async ({ target = null, newTitle = null } = {}) => {
    // Ensure we have a persisted save first — backend needs it.
    let activeSaveId = saveId;
    if (!activeSaveId || isDirty) {
      const saved = await save();
      if (!saved) return null;
      activeSaveId = saved.save_id;
    }
    const res = await fetch(`${API}/price-book/saves/${activeSaveId}/push-to-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        estimate_id: target || null,
        new_estimate_title: newTitle || null,
      }),
    });
    if (!res.ok) {
      const err = await res.text();
      toast.error(`Push failed — ${err.slice(0, 80)}`);
      return null;
    }
    const data = await res.json();
    if (data.mode === 'created') {
      toast.success(`New estimate ${data.estimate_number} created`);
    } else {
      toast.success('Appended to estimate');
    }
    setEstimateId(data.estimate_id);
    return data;
  }, [saveId, isDirty, save]);

  return {
    saveId,
    name, setName,
    estimateId, setEstimateId,
    isSaved, isDirty, loading, saving,
    save, pushToEstimate,
  };
}
